// ReactJs
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router'
// Node modules
import styled from 'styled-components'
import { get } from 'lodash'
// Contexts
import { AppContext } from '../../Context'
import { DeclarationContext, declarationData } from './DeclarationPage'
// Utils
import moment from 'moment'
import { getAttachByCategory } from '../../utils'
import { NumberFormatter } from '../../../common/util'
import { getBabysittingModeFrom } from '../../../common/BabysittingModes'
import { getBenefitAmountFrom } from '../../../common/BenefitAmount'
// Layouts
import PageLayout from '../../components/PageLayout'
import { Error, Sector } from '../../../common/Text'
import LabelField from '../../../common/LabelField'
import Checkbox from '../../../common/Checkbox'
import MonthYear from '../../../common/MonthYear'
import DeclarationFormHours from './DeclarationFormHours'
import DeclarationFormRefund from './DeclarationFormRefund'
import Uploader from '../../../common/Uploader'
// Services
import {
  deleteDeclarationAttachment,
  submitDeclaration,
  uploadDeclarationAttachment
} from '../../services/declaration-services'
import { getConsentLabel } from '../../services'


const DeclarationFormContainer = styled(PageLayout)`

  .declarations-sector {
    margin-bottom: 15px;
  }

  .declarations-separator {
    margin-top: 25px;
  }

  .declarations-separator-small {
    margin-top: 10px;
  }

  .declarations-input {
    margin-top: 20px;
  }

  .declarations-notice {
    margin-top: 5px;
  }

  .declarations-bold {
    font-weight: bold;
  }

  .declarations-bill-label {
    margin-top: 15px;
  }

  .declarations-checkbox {
    margin: 20px 0px 20px 0px;
  }

  .uploader {
    margin-bottom: 15px;
  }

`

export default function DeclarationForm({ ...props }) {

  const history = useHistory()
  const { month } = props.location.state
  const hoursFormatter = new NumberFormatter(3)

  const { record } = useContext(DeclarationContext)
  const { child, babysittingMode, hoursType } = record
  const { ageCategory } = child
  const {
    tokenId,
    attachmentConfig,
    babysittingModes,
    declarationAttachConfig,
    benefitAmounts
  } = useContext(AppContext)
  const [consentLabel, setconsentLabel] = useState(null)

  const [declaration, setDeclaration] = useState(null)
  const [attachments, setAttachments] = useState([])
  const [estimatedRefundAmount, setEstimatedRefundAmount] = useState(null)
  const [submitError, setSubmitError] = useState('')
  const [isNotSubmitting, setIsNotSubmitting] = useState(true)

  const monthlyInvoice = useMemo(() =>
    attachments && attachments.length > 0 && attachments.filter(attachment => attachment.category === 'monthly_invoice')[0],
    [attachments]
  )

  const monthlyInvoiceHasBeenUploaded = useMemo(() =>
    monthlyInvoice && monthlyInvoice.key && monthlyInvoice.name,
    [monthlyInvoice]
  )

  const hasUploaded = useMemo(() =>
    attachments && attachments.length > 0,
    [attachments]
  )

  const canSubmit = useMemo(() =>
    declaration
    && +declaration.hours > 0
    && monthlyInvoiceHasBeenUploaded
    && declaration.hasBeenSigned
    && isNotSubmitting,
    [declaration, monthlyInvoiceHasBeenUploaded, isNotSubmitting]
  )

  useEffect(() => {
    if (!declaration) {
      setDeclaration(month.declaration)
      setAttachments(month.declaration.attachments || [])
    }
  }, [month])


  useEffect(() => {
    const fetch = async () => {
      setconsentLabel(await getConsentLabel('declaration1'))
    }
    if ( !consentLabel ) {
      fetch()
    }
  }, [])

  const onChange = (key) => {
    return (value) => {
      const editedDeclaration = {
        ...declaration,
        [key]: value
      }
      setDeclaration(editedDeclaration)
    }
  }

  const onFileUpload = async (category, file) => {
    const result = await uploadDeclarationAttachment(declaration._id, declaration.recordId, category, file)
    if (result && result.errors) {
      throw new Error(result.errors[0].message)
    }
    if (result.data.uploadDeclarationAttachment) {
      setAttachments(result.data.uploadDeclarationAttachment.attachments)
    }
  }

  const onFileDelete = async (attachment) => {
    if (attachment) {
      const result = await deleteDeclarationAttachment(declaration._id, [attachment.key])
      if (result && result.errors) {
        throw new Error(result.errors[0].message)
      }
      if (result.data.removeDeclarationAttachment) {
        setAttachments(result.data.removeDeclarationAttachment.attachments)
      }
    }
  }

  const onFileClick = async (attachment) => {
    if (attachment) {
      window.open(`/beneficiary-api/rest/v1/declaration/${ declaration._id }/attachment/${ attachment.name }?key=${ attachment.key }&token=${ tokenId }`, '_blank')
    }
  }

  useEffect(() => {
    if (declaration && declaration.hours && hasUploaded) {
      const benefitAmount = getBenefitAmountFrom(benefitAmounts, ageCategory, babysittingMode, hoursType)
      const amount = declaration.hours * benefitAmount
      setEstimatedRefundAmount(amount)
    } else {
      setEstimatedRefundAmount(null)
    }
  }, [declaration, hasUploaded])

  const submit = async () => {
    setIsNotSubmitting(false)
    const { _id, recordId, hours } = declaration
    const declarationInput = `
      _id: "${ _id }"
      recordId: "${ recordId }"
      ${ hours ? `hours: ${ hours }` : `hours: 0` }
    `
    const response = await submitDeclaration(declarationInput, consentLabel, declarationData)
    if (response && response.errors) {
      setSubmitError(response.errors[0].message)
      setIsNotSubmitting(true)
    } else {
      setSubmitError('')
      history.push('/')
      return true
    }
  }

  return (
    <DeclarationFormContainer title="Je déclare mes frais de garde"
                              onPrevious={ () => history.push('/') }
                              actions={ {
                                submitTxt: 'Valider',
                                onSubmit: submit,
                                disableSubmit: !canSubmit
                              } }>

      <Sector className="declarations-sector">
        Mois de <MonthYear date={ month.date._d } fullName={ true }/>
      </Sector>
      <LabelField label="Nom" subLabelUnderline={ true }>{ child.firstName } { child.lastName }</LabelField>

      <Sector className="declarations-sector declarations-separator">
        { getBabysittingModeFrom(babysittingModes, record.babysittingMode, 'name') }
      </Sector>
      <DeclarationFormHours inputLabel="Nombre d’heures de garde payées (h)"
                            inputPlaceHolder="Non renseigné"
                            inputFormatter={ hoursFormatter }
                            value={ get(declaration, 'hours') }
                            onInputChange={ onChange('hours') }
                            comment={ <span>Pour les gardes décomptées en jours, nous vous rappelons que <b>1 jour = 9 heures</b>.</span> }/>

      <Sector className="declarations-sector declarations-separator">Facture mensuelle</Sector>
      {
        (declaration && attachmentConfig && declarationAttachConfig) && declarationAttachConfig.map(attachConfig =>
          <Uploader
            key={ attachConfig.category }
            value={ getAttachByCategory(attachments, attachConfig.category) }
            onFileUpload={ async (file) => {
              await onFileUpload(attachConfig.category, file)
            } }
            onFileDelete={ onFileDelete }
            onFileClick={ onFileClick }
            maxSize={ attachmentConfig.maxSize }
            accept={ attachmentConfig.accept }
            multiple={ true }
            optional={ attachConfig.optional }
            description={ <span>Taille maxi de { attachmentConfig.maxSize } Mo au format PNG, JPG ou PDF</span> }
            { ...props }
          />
        )
      }

      <Sector className="declarations-sector declarations-separator-small">
        Remboursement estimé
      </Sector>
      <DeclarationFormRefund amount={ estimatedRefundAmount }/>

      <Sector className="declarations-sector declarations-separator">
        Signature
      </Sector>
      <LabelField label="Date" subLabelUnderline={ true }>{ moment().format('DD/MM/YYYY') }</LabelField>
      <Checkbox checked={ get(declaration, 'hasBeenSigned') } onChange={ onChange('hasBeenSigned') } position="right"
                className="declarations-checkbox">
        { consentLabel }
      </Checkbox>

      { submitError && <Error>{ submitError }</Error> }

    </DeclarationFormContainer>

  )

}
